/**
 * Frontend Analytics Tracker
 *
 * - Tracks pageviews, custom events, JS errors
 * - Respects cookie consent (only tracks if analytics consent is given)
 * - Uses sendBeacon for reliability on page unload
 * - Automatic session ID (30 min inactivity timeout)
 *
 * Usage:
 *   import { tracker } from './utils/tracker';
 *   tracker.pageview('/products');
 *   tracker.event('add_to_cart', 'ecommerce', { product_id: 42 });
 *   tracker.error(new Error('oops'));
 */

const TRACK_API = 'https://khfsazxate.eu-central-1.awsapprunner.com/api/track.php';
const SESSION_KEY = 'cp_analytics_session';
const SESSION_TTL_MS = 30 * 60 * 1000; // 30 min

function hasAnalyticsConsent() {
  try {
    const raw = localStorage.getItem('cp_cookie_consent');
    if (!raw) return false;
    const consent = JSON.parse(raw);
    return !!consent.analytics;
  } catch {
    return false;
  }
}

function generateId() {
  return 's_' + Date.now().toString(36) + '_' + Math.random().toString(36).substring(2, 10);
}

function getSessionId() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (raw) {
      const s = JSON.parse(raw);
      if (s.id && s.lastActive && Date.now() - s.lastActive < SESSION_TTL_MS) {
        s.lastActive = Date.now();
        localStorage.setItem(SESSION_KEY, JSON.stringify(s));
        return s.id;
      }
    }
  } catch {}
  const id = generateId();
  localStorage.setItem(SESSION_KEY, JSON.stringify({ id, lastActive: Date.now() }));
  return id;
}

function send(payload) {
  if (!hasAnalyticsConsent()) return;
  const body = JSON.stringify({
    ...payload,
    session_id: getSessionId(),
    language: document.documentElement.lang || navigator.language,
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,
  });

  try {
    // Use fetch with credentials: 'omit' to avoid CORS issues with Allow-Origin: *
    fetch(TRACK_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
      credentials: 'omit',
      mode: 'cors',
    }).catch(() => {});
  } catch {}
}

// Track URL params for UTM etc.
function parseUTM() {
  try {
    const params = new URLSearchParams(window.location.search);
    return {
      utm_source: params.get('utm_source') || undefined,
      utm_medium: params.get('utm_medium') || undefined,
      utm_campaign: params.get('utm_campaign') || undefined,
    };
  } catch {
    return {};
  }
}

let lastPageviewPath = null;
let lastPageviewTime = null;

export const tracker = {
  pageview(path, title) {
    const now = Date.now();
    const duration = lastPageviewTime ? now - lastPageviewTime : null;

    send({
      type: 'pageview',
      path: path || window.location.pathname + window.location.search,
      title: title || document.title,
      referrer: document.referrer || undefined,
      duration_ms: duration,
      ...parseUTM(),
    });

    lastPageviewPath = path;
    lastPageviewTime = now;
  },

  event(eventName, category = 'general', data = {}) {
    send({
      type: 'event',
      event_name: eventName,
      event_category: category,
      event_data: data,
      path: window.location.pathname,
    });
  },

  error(error, extra = {}) {
    const msg = error && error.message ? error.message : String(error);
    send({
      type: 'error',
      level: extra.level || 'error',
      message: msg,
      stack: error && error.stack ? error.stack : '',
      path: window.location.pathname,
      ...extra,
    });
  },
};

// Global error handler
if (typeof window !== 'undefined') {
  window.addEventListener('error', (e) => {
    tracker.error(e.error || new Error(e.message), {
      line: e.lineno,
      column: e.colno,
    });
  });

  window.addEventListener('unhandledrejection', (e) => {
    tracker.error(e.reason || new Error('Unhandled promise rejection'));
  });
}

export default tracker;
